import { useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Controller, Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import {
  Column,
  Divider,
  FieldValue,
  InputOfficeDropdown,
  InputSelect,
  Label,
  MultiSelectGroup,
  NumberField,
  RadioOption,
  Row,
  Text,
} from '~/commonComponents';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';
import { useAppSelector, useLoading, useUserSelector } from '~/hooks';
import { getAllProfessionTypeOptions } from '~/utils/DentistUtils';
import { filterObjectKeys } from '~/utils/Object';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { PayType, CompensationType } from '~/domain/PermanentJobs/enums';
import {
  jobTitleMaxLength,
  benefitsOptions,
  benefitsOtherMaxLength,
} from '~/components/PermanentJobsV2/utils';
import type { IJobDetailsForm } from '../Types';
import { EnumHelper } from '~/utils/EnumHelper';

interface IGeneralJobInfoFormProps {
  control: Control<IJobDetailsForm>;
  errors: FieldErrors<IJobDetailsForm>;
  setValue: UseFormSetValue<IJobDetailsForm>;
  onFieldChange?: (newValue: any, fieldOnChange: (value: any) => void) => void;
}

export const GeneralJobInfoForm = ({
  control,
  errors,
  setValue,
  onFieldChange,
}: IGeneralJobInfoFormProps) => {
  const user = useUserSelector();
  const isParentOffice = useIsParentOfficeSelector();
  const allProfessions = useAppSelector((state) => state.user.allProfessions);
  const userEducation = useAppSelector((state) => state.user.userEducation);

  const { isLoading } = useLoading(['GET_USER_EDUCATION_ACTION']);

  // Watch specific fields for reactive logic
  const office = useWatch({ control, name: 'office' });
  const professionType = useWatch({ control, name: 'professionType' });
  const officeSpecialty = useWatch({ control, name: 'officeSpecialty' });
  const compensationType = useWatch({ control, name: 'compensationType' });
  const benefits = useWatch({ control, name: 'benefits' });
  const benefitsOther = useWatch({ control, name: 'benefitsOther' });
  const payType = useWatch({ control, name: 'payType' });

  const allProfessionOptions = getAllProfessionTypeOptions(allProfessions, { showEfda: true });

  // Get specialties based on selected profession type
  const specialtyOptions = useMemo(() => {
    if (!professionType || !userEducation) {
      return [];
    }

    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(professionType);
    const professionCode = isSubProfession
      ? UserSubProfession.getProfession(professionType)
      : professionType;
    const normalizedProfessionCode = UserProfession.getNormalizedCode(professionCode || '');
    const specialtiesAndProcedures =
      // @ts-expect-error - not typed
      userEducation?.[normalizedProfessionCode]?.specialtiesAndProcedures;

    if (!specialtiesAndProcedures) {
      return [];
    }

    const filteredSpecialties = filterObjectKeys(specialtiesAndProcedures, 'specialty');
    const specialtyKeys = Object.keys(filteredSpecialties);

    return specialtyKeys.map((key) => ({
      title: key.split('_')?.[1] || 'General',
      value: key,
    }));
  }, [professionType, userEducation]);

  const payRateLabel = payType === PayType.SALARY ? '/year' : '/hour';

  // Set default office
  useEffect(() => {
    if (user && !office) {
      setValue('office', user.id);
    }
  }, [user, office, setValue]);

  // Validate and reset specialty when profession type changes
  useEffect(() => {
    if (!professionType || specialtyOptions.length === 0) return;

    // Check if current specialty is valid in the new options
    const isCurrentSpecialtyValid = specialtyOptions.some(
      (option) => option.value === officeSpecialty,
    );

    if (!isCurrentSpecialtyValid) {
      const filteredSpecialties = filterObjectKeys(
        user?.office_info?.education?.specialtiesAndProcedures,
        'specialty',
      );
      const userSpecialty = Object.keys(filteredSpecialties)?.[0];

      // Reset to user's specialty if available, otherwise default to first option
      const defaultSpecialty = specialtyOptions.some((option) => option.value === userSpecialty)
        ? userSpecialty
        : specialtyOptions[0]?.value;

      if (defaultSpecialty) {
        setValue('officeSpecialty', defaultSpecialty);
      }
    }
    // If valid, keep the current specialty (do nothing)
  }, [
    professionType,
    specialtyOptions,
    officeSpecialty,
    setValue,
    user?.office_info?.education?.specialtiesAndProcedures,
  ]);

  // Clear maxRate when changing to FIXED compensation
  const handleCompensationTypeChange = (type: CompensationType) => {
    setValue('compensationType', type);
    if (type === CompensationType.FIXED) {
      setValue('maxRate', '');
    }
  };

  // Clear benefitsOther when "Other" is deselected
  useEffect(() => {
    if (!benefits?.includes('Other') && benefitsOther) {
      setValue('benefitsOther', '');
    }
  }, [benefits, benefitsOther, setValue]);

  return (
    <Column gap={24}>
      <Column>
        <Label text="Office Name" required={isParentOffice} />
        {isParentOffice ? (
          <Controller
            control={control}
            name="office"
            render={({ field }) => (
              <InputOfficeDropdown
                placeholder="Enter or Select Office Name"
                value={field.value || ''}
                onChange={(item) => {
                  if (onFieldChange) {
                    onFieldChange(item?.value || '', field.onChange);
                  } else {
                    field.onChange(item?.value || '');
                  }
                }}
                error={!!errors.office}
              />
            )}
          />
        ) : (
          <Text paddingLeft={17}>{user?.office_name || ''}</Text>
        )}
      </Column>

      <Divider marginVertical={0} />

      <Column>
        <Controller
          control={control}
          name="jobTitle"
          render={({ field }) => (
            <FieldValue
              label="Job Title"
              placeholder="Enter Job Title"
              required
              shouldCapitalize={false}
              value={field.value}
              setValue={field.onChange}
              customInputStyle={{ width: '100%', boxSizing: 'border-box' }}
              validation={{ maxLength: jobTitleMaxLength }}
              error={!!errors.jobTitle}
              errorStyle="border"
            />
          )}
        />
        <Text marginTop={8} color="neutral_500" italic>
          {jobTitleMaxLength} Character Max
        </Text>
      </Column>

      <Divider marginVertical={0} />

      <Column>
        <Label text="Professional Type" required />
        <Controller
          control={control}
          name="professionType"
          render={({ field }) => (
            <InputSelect
              placeholder="Select Professional Type"
              value={field.value}
              setValue={(value) => {
                if (onFieldChange) {
                  onFieldChange(value, field.onChange);
                } else {
                  field.onChange(value);
                }
              }}
              isSelectVisible={false}
              options={(allProfessionOptions || []).map((profession) => ({
                title: profession?.name || '',
                value: profession?.code || '',
              }))}
              error={!!errors.professionType}
            />
          )}
        />
      </Column>

      <Column>
        <Label text="Specialty" required />
        <Controller
          control={control}
          name="officeSpecialty"
          render={({ field }) => (
            <InputSelect
              placeholder="Select Specialty"
              value={field.value}
              setValue={(value) => {
                if (onFieldChange) {
                  onFieldChange(value, field.onChange);
                } else {
                  field.onChange(value);
                }
              }}
              isSelectVisible={false}
              options={specialtyOptions}
              disabled={!professionType}
              customTitle={field.value ? field.value.split('_')?.[1] || 'General' : undefined}
              error={!!errors.officeSpecialty}
              isLoading={isLoading}
            />
          )}
        />
      </Column>

      <Divider marginVertical={0} />

      <Column gap={12}>
        <Label text="Pay Type" required={false} />
        <Row gap={24}>
          <RadioOption
            text="Salary"
            checked={payType === PayType.SALARY}
            onClick={() => setValue('payType', PayType.SALARY)}
          />
          <RadioOption
            text="Hourly"
            checked={payType === PayType.HOURLY}
            onClick={() => setValue('payType', PayType.HOURLY)}
          />
        </Row>
      </Column>

      <Column gap={12}>
        <Label text="Compensation" required={false} />
        <Column gap={16}>
          <RadioOption
            text="Fixed Amount"
            checked={compensationType === CompensationType.FIXED}
            onClick={() => handleCompensationTypeChange(CompensationType.FIXED)}
          />
          <RadioOption
            text="Pay Range (Min - Max)"
            checked={compensationType === CompensationType.RANGE}
            onClick={() => handleCompensationTypeChange(CompensationType.RANGE)}
          />
        </Column>
      </Column>

      {compensationType && (
        <Column>
          <Label
            text={`Stated Pay ${compensationType === CompensationType.RANGE ? 'Range' : ''}`}
            required
          />
          <Row alignItems="center" gap={8}>
            <Text>$</Text>
            <Controller
              control={control}
              name="minRate"
              render={({ field }) => (
                <NumberField
                  placeholder="Amount"
                  value={field.value}
                  setValue={field.onChange}
                  currency
                  customInputStyle={{
                    width: compensationType === CompensationType.FIXED ? '200px' : '150px',
                  }}
                  error={!!errors.minRate}
                />
              )}
            />
            {compensationType === CompensationType.RANGE && (
              <>
                <Text>-</Text>
                <Text>$</Text>
                <Controller
                  control={control}
                  name="maxRate"
                  render={({ field }) => (
                    <NumberField
                      placeholder="Amount"
                      value={field.value}
                      setValue={field.onChange}
                      currency
                      customInputStyle={{ width: '150px' }}
                      error={!!errors.maxRate}
                    />
                  )}
                />
              </>
            )}
            <Text fontSize={18} bold>
              {payRateLabel}
            </Text>
          </Row>
        </Column>
      )}

      <Column gap={12}>
        <Label text="Benefits Information" required={false} />
        <Text color="neutral_500" italic>
          <Controller
            control={control}
            name="benefits"
            render={({ field }) => (
              <MultiSelectGroup
                value={field.value}
                options={benefitsOptions.map((option) => ({
                  value: option.code,
                  label: option.name,
                }))}
                onChange={(selected) => field.onChange(selected.map((s) => s.value))}
              />
            )}
          />
        </Text>
        <AnimatePresence mode="wait">
          {benefits?.includes('Other') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Column marginTop={8} gap={10}>
                <Text>Enter any other benefits below:</Text>
                <Controller
                  control={control}
                  name="benefitsOther"
                  render={({ field }) => (
                    <textarea
                      {...field}
                      placeholder="Enter other benefits here"
                      className="textarea-style"
                      maxLength={benefitsOtherMaxLength}
                      style={{
                        width: 'auto',
                        height: 60,
                        fontSize: 16,
                      }}
                    />
                  )}
                />
              </Column>
            </motion.div>
          )}
        </AnimatePresence>
      </Column>
    </Column>
  );
};
